<template>
  <CRow>
    <CCol col>
      <CCard>
        <CCardHeader>
          <CRow class="justify-content-between px-3 align-items-center">
            <h5>Promotion Codes</h5>
            <CButton
              v-if="canCreatePromotionCode"
              color="primary"
              @click="showCreate = true"
              >Create</CButton
            >
          </CRow>
        </CCardHeader>
        <CCardBody>
          <CDataTable
            hover
            striped
            border
            :loading="loading"
            :items="promotionCodes"
            :fields="fields"
            :items-per-page="pagination.limit"
            :pagination="false"
          >
            <td slot="status" slot-scope="{ item }">
              {{ formatStatus(item.status) }}
            </td>
            <td slot="restriction" slot-scope="{ item }">
              {{ formatRestriction(item.restriction) }}
            </td>
            <td slot="limit" slot-scope="{ item }">
              {{ item.limit == null ? 'None' : item.limit }}
            </td>
            <td slot="discount" slot-scope="{ item }">
              {{ formatDiscount(item) }}
            </td>
            <td slot="effective_date" slot-scope="{ item }">
              {{ formatEffectiveDate(item) }}
            </td>
            <td slot="created_at" slot-scope="{ item }">
              {{ item.created_at | moment(datetimeFormat) }}
            </td>
            <td slot="actions" slot-scope="{ item }">
              <CButton size="sm" color="danger" @click="confirmDelete(item)">
                Delete
              </CButton>
            </td>
          </CDataTable>
          <div
            v-if="!loading && promotionCodes.length === 0"
            class="text-center py-3"
          >
            No promotion codes found.
          </div>
          <MyPagination
            :limit="pagination.limit"
            :total="pagination.total"
            :activePage="pagination.page"
            @changePage="onChangePage"
            @changePageSize="onChangePageSize"
          />
        </CCardBody>
      </CCard>
      <CreatePromotionCodeModal
        v-if="canCreatePromotionCode"
        :visible.sync="showCreate"
        @created="onCreated"
      />
    </CCol>
  </CRow>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import { Common } from '@/constants';
import {
  ACTION_DELETE_PROMOTION_CODE,
  ACTION_FETCH_PROMOTION_CODES,
} from '@/store/actions.type';
import MyPagination from '@/components/MyPagination.vue';
import CreatePromotionCodeModal from './CreatePromotionCodeModal.vue';
import { hasPermission } from '@/utils/permissions';

export default {
  name: 'PromotionCodes',
  components: { MyPagination, CreatePromotionCodeModal },
  data() {
    return {
      loading: false,
      showCreate: false,
      datetimeFormat: Common.datetimeFormat,
      fields: [
        { key: 'status', label: 'Status' },
        { key: 'code', label: 'Code' },
        { key: 'restriction', label: 'Restriction' },
        { key: 'limit', label: 'Limit' },
        { key: 'current_count', label: 'Current count' },
        { key: 'discount', label: 'Discount' },
        { key: 'effective_date', label: 'Effective date' },
        { key: 'created_at', label: 'Code created date and time' },
        { key: 'actions', label: '' },
      ],
      pagination: {
        page: Number((this.$route && this.$route.query.page) || 1),
        limit: Number((this.$route && this.$route.query.limit) || 25),
        total: 0,
      },
    };
  },
  computed: {
    ...mapGetters(['promotionCodes', 'pagingPromotionCodes', 'permissions']),
    canCreatePromotionCode() {
      return hasPermission(this.permissions, 'admin');
    },
  },
  mounted() {
    this.fetchCodes();
  },
  methods: {
    updateURL() {
      this.$router.push({
        query: { page: this.pagination.page, limit: this.pagination.limit },
      });
    },
    fetchCodes() {
      this.loading = true;
      const query = {
        page: this.pagination.page,
        limit: this.pagination.limit,
      };
      return this.$store
        .dispatch(ACTION_FETCH_PROMOTION_CODES, query)
        .then(() => {
          const paging = this.pagingPromotionCodes || {};
          this.pagination.total = Number(paging.total_rows || 0);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onChangePage(page) {
      this.pagination.page = page;
      this.updateURL();
      this.fetchCodes();
    },
    onChangePageSize(limit) {
      this.pagination = { page: 1, limit, total: this.pagination.total };
      this.updateURL();
      this.fetchCodes();
    },
    onCreated() {
      this.showCreate = false;
      return this.fetchCodes();
    },
    confirmDelete(item) {
      this.$confirm({
        message: `Delete promotion code ${item.code}?`,
        button: { no: 'No', yes: 'Yes' },
        callback: (confirmed) => {
          if (confirmed) this.deleteCode(item.id);
        },
      });
    },
    deleteCode(id) {
      return this.$store.dispatch(ACTION_DELETE_PROMOTION_CODE, id).then(() => {
        if (this.promotionCodes.length === 1 && this.pagination.page > 1) {
          this.pagination.page -= 1;
          this.updateURL();
        }
        return this.fetchCodes();
      });
    },
    formatDiscount(item) {
      if (item.discount_type === 'percentage') {
        return `${item.discount_value}%`;
      }
      return `$${(Number(item.discount_value || 0) / 100).toFixed(2)}`;
    },
    formatStatus(status) {
      return status === 'active' ? 'Active' : 'Inactive';
    },
    formatRestriction(restriction) {
      const labels = {
        none: 'None',
        first_booking: 'First booking',
        one_time: 'One time',
      };
      return labels[restriction] || restriction || '-';
    },
    formatEffectiveDate(item) {
      if (item.always) return 'Always';
      return `${moment(item.effective_from).format(
        'YYYY-MM-DD HH:mm'
      )} to ${moment(item.effective_to).format('YYYY-MM-DD HH:mm')}`;
    },
  },
};
</script>
